import { usePermissions } from '@shared/hooks/usePermissions';
import { ErrorState } from '../../../List/components/feedback';
import { useOrder } from '../../hooks/useOrder';
import { AdditionalInfo } from '../display/AdditionalInfo';
import { Breadcrumb } from '../display/Breadcrumb';
import { DeliveryRate } from '../display/DeliveryRate';
import { ErrorAlert } from '../display/ErrorAlert';
import { HeaderButtons } from '../display/HeaderButtons';
import { OrderInfo } from '../display/OrderInfo';
import { PaymentCorrections } from '../display/PaymentCorrections';
import { OrderProgress } from '../display/OrderProgress';
import { OrderStockMovements } from '../display/OrderStockMovements';
import { SelectedItems } from '../display/SelectedItems';
import { ShippingInfo } from '../display/ShippingInfo';
import { LogisticStatus } from '../display/LogisticStatus';
import { Tracking } from '../display/Tracking';
import { ViewOrdersSkeleton } from '../skeletons';

export const Main = () => {
  const { order, loading, error, refetch } = useOrder();
  const { getTeamType } = usePermissions();
  const isTenant3pl = getTeamType() === 'tenant_3pl';

  if (loading) {
    return <ViewOrdersSkeleton showPreparation={isTenant3pl} />;
  }

  if (error) {
    // Creamos un objeto Error para cumplir con la prop de ErrorState
    const errorObj = new Error(error);
    return <ErrorState error={errorObj} onRetry={refetch} />;
  }

  return (
    <>
      <Breadcrumb
        orderRef={order?.ref}
        orderStatusDescription={order?.status.description}
        orderStatusId={order?.status.id}
      />
      <HeaderButtons order={order} onActionSuccess={refetch} />
      <div className="py-4 px-6">
        <div className="flex flex-col md:flex-row justify-between items-start gap-6">
          {/* Columna Izquierda */}
          <div className="w-full md:w-[70%] flex flex-col gap-5">
            <ErrorAlert
              errors={order?.errors}
              customer={order?.customer}
              orderData={order ?? undefined}
              onRefetch={refetch}
            />
            <OrderProgress
              orderCreatedAt={order?.order_created_at}
              readyToPrepareAt={order?.ready_to_prepare_at}
              processedAt={order?.processed_at ?? order?.shipment?.processed_at}
              showPreparation={isTenant3pl}
              preparation={order?.preparation}
            />
            <OrderInfo
              orderRef={order?.ref}
              shopName={order?.shop.name}
              shopImageHash={order?.shop.image_hash}
              showPreparation={isTenant3pl}
              preparation={order?.preparation}
            />
            <SelectedItems
              lines={order?.lines}
              packagings={isTenant3pl ? order?.order_packagings : undefined}
              totalShipping={order?.total_shipping}
              totalTax={order?.total_tax}
              totalDiscount={order?.total_discount}
              currency={order?.currency}
              orderId={order?.id}
              statusId={order?.status_id}
              onUpdate={refetch}
            />
            {isTenant3pl && <OrderStockMovements orderId={order?.id} />}
          </div>
          {/* Columna Derecha */}
          <div className="w-full md:w-[30%] flex flex-col gap-5">
            <DeliveryRate />
            <PaymentCorrections />
            <LogisticStatus shipment={order?.shipment} statusId={order?.status_id} />
            {order?.shipment?.tracking_number && <Tracking shipment={order?.shipment} />}
            <ShippingInfo
              customer={order?.customer}
              orderId={order?.id}
              lines={order?.lines}
              statusId={order?.status_id}
              onUpdate={refetch}
            />
            <AdditionalInfo
              paymentMethodId={order?.payment_method_id}
              orderId={order?.id}
              lines={order?.lines}
              statusId={order?.status_id}
              onUpdate={refetch}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default Main;
